import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, LineChart, Terminal, BookText, LogOut, Activity, Radar,
  Briefcase, Shield, SlidersHorizontal, BarChart3, Users, Bell, Trophy, FlaskConical,
  Sparkles, Settings as SettingsIcon, Eye, Brain, Heart, GraduationCap, Cpu, Telescope,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useAccounts } from "@/context/AccountContext";
import {
  api, isImpersonating as isImpersonatingLS, getImpersonationMeta, stopImpersonation,
} from "@/lib/api";
import { useWebSocket } from "@/lib/ws";
import AccountPicker from "@/components/AccountPicker";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/ati", label: "Intelligence", icon: Brain, testId: "nav-ati" },
  { to: "/health-score", label: "Health Score", icon: Heart, testId: "nav-health-score" },
  { to: "/coach", label: "AI Coach", icon: GraduationCap, testId: "nav-coach" },
  { to: "/optimizer", label: "Optimizer", icon: Cpu, testId: "nav-optimizer" },
  { to: "/prediction", label: "Prediction", icon: Telescope, testId: "nav-prediction" },
  { to: "/scanner", label: "Scanner", icon: Radar, testId: "nav-scanner" },
  { to: "/signals", label: "Signals", icon: LineChart, testId: "nav-signals" },
  { to: "/straddles", label: "Straddles", icon: Sparkles, testId: "nav-straddles" },
  { to: "/portfolio", label: "Portfolio", icon: Briefcase, testId: "nav-portfolio" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testId: "nav-analytics" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testId: "nav-leaderboard" },
  { to: "/backtest", label: "Backtest", icon: FlaskConical, testId: "nav-backtest" },
  { to: "/prop-challenge", label: "Prop Challenge", icon: Trophy, testId: "nav-prop-challenge" },
  { to: "/risk", label: "Risk", icon: Shield, testId: "nav-risk" },
  { to: "/strategies", label: "Strategies", icon: SlidersHorizontal, testId: "nav-strategies" },
  // Phase 2.5 — Unified Connect MT5 (replaces separate Accounts + Connect EA nav items).
  { to: "/connect", label: "Connect MT5", icon: Terminal, testId: "nav-connect", adminOnly: true },
  { to: "/alerts", label: "Alerts", icon: Bell, testId: "nav-alerts", adminOnly: true },
  { to: "/journal", label: "Journal", icon: BookText, testId: "nav-journal" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings", adminOnly: true },
];

export const AppLayout = () => {
  const { user, logout } = useAuth();
  const { activeAccountId } = useAccounts();
  const [eaOnline, setEaOnline] = useState(false);
  const [lastHb, setLastHb] = useState(null);
  const [autoExec, setAutoExec] = useState(false);

  const fetchStatus = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/risk/settings").catch(() => ({ data: {} })),
      ]);
      setEaOnline(!!r.data?.ea_status?.is_online);
      setLastHb(r.data?.ea_status?.last_heartbeat || null);
      setAutoExec(!!s.data?.auto_execute_enabled);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, 15000);
    // Phase 7A — listen for auto-execute toggle events fired by ExecutionHealthCard
    // so the top-bar pill reflects the new state immediately instead of waiting
    // for the next 15-s poll tick.
    const onAutoExec = (e) => {
      if (typeof e?.detail?.enabled === "boolean") setAutoExec(e.detail.enabled);
      fetchStatus();
    };
    window.addEventListener("veerox:auto-execute-changed", onAutoExec);
    return () => {
      clearInterval(iv);
      window.removeEventListener("veerox:auto-execute-changed", onAutoExec);
    };
  }, [activeAccountId]);

  useWebSocket((msg) => {
    if (msg.type === "ea.status" && (!msg.data?.account_id || msg.data.account_id === activeAccountId)) {
      setEaOnline(!!msg.data?.is_online);
      setLastHb(msg.data?.last_heartbeat || null);
    }
  });

  return (
    <div className="min-h-screen flex flex-col bg-[#0a0e1a] text-[#e5e7eb]">
      {/* Phase 8A — Impersonation banner (yellow, persistent) */}
      {isImpersonatingLS() && (
        <div
          className="w-full px-4 py-2 flex items-center justify-between text-xs font-mono"
          style={{ background: "linear-gradient(90deg, #fbbf2422, #fbbf2411)", borderBottom: "1px solid #fbbf24" }}
          data-testid="user-side-impersonation-banner"
        >
          <div className="flex items-center gap-2 text-[#fbbf24]">
            <Eye className="w-3.5 h-3.5" />
            <span className="font-bold">IMPERSONATING</span>
            <span className="text-[#e5e7eb]">{getImpersonationMeta()?.target?.email || "(user)"}</span>
            <span className="text-[#9ca3af]">· viewing their dashboard · short-lived (30 min)</span>
          </div>
          <button
            onClick={() => {
              stopImpersonation();
              toast.success("Exited impersonation");
              window.location.href = "/admin/users";
            }}
            className="px-2 py-0.5 rounded border border-[#fbbf24]/40 hover:bg-[#fbbf24]/10 text-[#fbbf24] font-bold"
            data-testid="user-side-exit-impersonation-btn"
          >
            EXIT · Back to Admin ×
          </button>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        <aside className="w-60 shrink-0 border-r border-[#1f2937] bg-[#0a0e1a] flex flex-col" data-testid="app-sidebar">
        <div className="h-16 flex items-center px-5 border-b border-[#1f2937]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#3b82f6]/10 border border-[#3b82f6]/40 flex items-center justify-center">
              <Activity className="w-4 h-4 text-[#3b82f6]" strokeWidth={2} />
            </div>
            <div>
              <div className="font-heading font-black tracking-tighter text-lg leading-none">VEEROX</div>
              <div className="vx-label leading-none mt-1">Trading OS</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-[#3b82f6]/10 text-white border-l-2 border-[#3b82f6] pl-[10px]"
                    : "text-[#9ca3af] hover:bg-[#1f2937] hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.75} />
              <span className="font-medium">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-3 border-t border-[#1f2937] text-[10px] text-[#6b7280] font-mono">
          v0.6.0 · Phase 6
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-[#1f2937] bg-[#0a0e1a] flex items-center justify-between px-6" data-testid="app-topbar">
          <div className="flex items-center gap-3 min-w-0">
            <AccountPicker />
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1f2937] bg-[#111827]"
              data-testid="ea-status-pill"
            >
              <span
                className={`w-2 h-2 rounded-full ${eaOnline ? "bg-[#10b981] vx-dot-online" : "bg-[#ef4444]"}`}
                data-testid="ea-status-dot"
              />
              <span className="text-xs font-mono">EA {eaOnline ? "ONLINE" : "OFFLINE"}</span>
              {lastHb && (
                <span className="text-[10px] text-[#6b7280] font-mono">
                  {new Date(lastHb).toLocaleTimeString()}
                </span>
              )}
            </div>
            <div
              className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-mono text-xs font-bold ${
                autoExec ? "" : "vx-pulse-warn"
              }`}
              style={{
                borderColor: autoExec ? "#10b981" : "#ef4444",
                background: autoExec ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.12)",
                color: autoExec ? "#10b981" : "#ef4444",
              }}
              data-testid="auto-exec-pill"
              title={autoExec
                ? "Auto-Execute is ON — approved signals will place live trades."
                : "Auto-Execute is OFF — trades will NOT execute. Enable it in the Trade Execution Health card."}
            >
              <span className={`w-2 h-2 rounded-full ${autoExec ? "bg-[#10b981]" : "bg-[#ef4444]"}`} />
              AUTO-EXECUTE {autoExec ? "ON ✓" : "OFF ⚠"}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user?.role === "admin" && !user?._is_impersonating && (
              <div
                className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full border font-mono text-[10px] font-bold tracking-widest"
                style={{ borderColor: "#dc2626", color: "#dc2626", background: "rgba(220,38,38,0.08)" }}
                data-testid="owner-indicator-pill"
                title="You are logged in as the platform owner. Click 'Switch to Admin Panel' in the top bar to access admin console."
              >
                <Shield className="w-3 h-3" />
                OWNER
              </div>
            )}
            <div className="text-right hidden md:block">
              <div className="text-sm font-medium" data-testid="topbar-user-name">{user?.name}</div>
              <div className="text-[10px] text-[#6b7280] font-mono">{user?.email}</div>
            </div>
            {user?.role === "admin" && !user?._is_impersonating && (
              <button
                onClick={() => (window.location.href = "/admin")}
                data-testid="switch-to-admin-btn"
                title="Switch to Admin Panel"
                className="hidden md:flex items-center gap-1.5 px-3 h-9 rounded-md border border-[#dc2626]/40 hover:bg-[#dc2626]/10 text-[#dc2626] font-mono text-[11px] font-bold tracking-widest"
              >
                <Shield className="w-3 h-3" /> ADMIN →
              </button>
            )}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="w-9 h-9 flex items-center justify-center rounded-md border border-[#1f2937] hover:bg-[#1f2937] transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4 text-[#9ca3af]" strokeWidth={1.75} />
            </button>
          </div>
        </header>
        <main className="flex-1 min-h-0 overflow-auto p-6">
          {/* key on Outlet forces every routed page to remount when active account changes → data auto-refreshes */}
          <Outlet key={activeAccountId || "default"} />
        </main>
      </div>
      </div>
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "#111827", color: "#e5e7eb", border: "1px solid #1f2937" }
      }} />
    </div>
  );
};
